import { FormEvent, RefObject } from "react";
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon, QrCodeIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { ITillStockSearchResult } from "../../models/till/ITillStockSearch";
import LoadingWheel from "../shared/LoadingWheel";
import TillResultCard from "./TillResultCard";

interface TillSearchPaneProps {
  query: string;
  onQueryChange: (value: string) => void;
  onSubmit: () => void;
  results: ITillStockSearchResult[] | undefined;
  /** How many the API was asked for; hitting it means the list is truncated. */
  take: number;
  isLoading: boolean;
  inBasketIds: Set<string>;
  onAdd: (partId: string) => void;
  inputRef: RefObject<HTMLInputElement>;
  onOpenFullSearch: () => void;
  disabled?: boolean;
}

/** Scan-or-type search box with a grid of touch-sized results. */
export default function TillSearchPane({
  query, onQueryChange, onSubmit, results, take, isLoading, inBasketIds, onAdd, inputRef, onOpenFullSearch, disabled,
}: TillSearchPaneProps) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const showResults = query.trim().length >= 2;

  return (
    <section className="flex h-full flex-col min-h-0">
      <form onSubmit={handleSubmit} className="p-3 border-b border-gray-200 bg-white">
        <label htmlFor="till-search" className="sr-only">Search stock</label>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400" />
          <input
            id="till-search"
            ref={inputRef}
            type="search"
            inputMode="search"
            autoFocus
            autoComplete="off"
            value={query}
            disabled={disabled}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Scan a label or type a tag number, part or vehicle"
            className="block w-full h-14 rounded-lg border-gray-300 pl-12 pr-12 text-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
          />
          {query ? (
            <button
              type="button"
              onClick={() => { onQueryChange(""); inputRef.current?.focus(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          ) : null}
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="flex items-center gap-1 text-xs text-gray-500">
            <QrCodeIcon className="h-4 w-4 shrink-0" />
            Scanning a part label adds it straight to the basket. Press Enter on an exact tag to add it.
          </p>
          <button
            type="button"
            onClick={onOpenFullSearch}
            disabled={disabled}
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            Full stock search
          </button>
        </div>
      </form>

      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {!showResults ? (
          <p className="mt-10 text-center text-gray-400">Start typing to search unsold stock.</p>
        ) : isLoading && !results ? (
          <LoadingWheel />
        ) : !results || results.length === 0 ? (
          <p className="mt-10 text-center text-gray-500">No unsold parts match &ldquo;{query.trim()}&rdquo;.</p>
        ) : (
          <>
            {results.length >= take ? (
              <p className="mb-2 text-xs text-gray-500">Showing the first {take} matches - keep typing to narrow it down.</p>
            ) : null}
            <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2">
              {results.map((part) => (
                <TillResultCard key={part.id} part={part} inBasket={inBasketIds.has(part.id.toLowerCase())} onAdd={onAdd} disabled={disabled} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
